import {
    Component,
    Directive,
    Input,
    NgZone,
    OnChanges,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';

interface DataTemplateContext {
    tpl: TemplateRef<any>;
    ctx?: any;
}

export interface ScreenReaderTableOptions {
    role?: 'table' | 'grid' | string;
    label?: string;
}

export interface ScreenReaderTableData {
    headerRows?: Array<Array<string | DataTemplateContext>>;
    footerRows?: Array<Array<string | DataTemplateContext>>;
    bodyRows?: Array<Array<string | DataTemplateContext>>;
}

@Directive({
    selector: '[cmpsScreenReaderTableSection]',
})
export class ScreenReaderTableSectionDirective implements OnChanges {
    @Input('cmpsScreenReaderTableSection') data: Array<
        Array<string | DataTemplateContext>
    >;

    @Input('cmpsScreenReaderTableSectionUseTh') useTh: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        public viewContainerRef: ViewContainerRef,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('options') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('data')
        ) {
            this._render();
        }
    }

    private _render(): void {
        if (!this.data) return;
        this._ngZone.runOutsideAngular(() => {
            this.viewContainerRef.clear();
            const container =
                this.viewContainerRef.element.nativeElement.parentNode;
            Array.from(container.children).forEach((row) =>
                container.removeChild(row),
            );
            this.data.forEach((row) => {
                const tr = document.createElement('tr');
                row.forEach((cell: any) => {
                    if (cell && typeof cell === 'object') {
                        const vr = this.viewContainerRef.createEmbeddedView(
                            cell.tpl,
                            cell.ctx,
                        );
                        vr.rootNodes.forEach((value) => tr.appendChild(value)); // TODO revise
                    } else {
                        const td = document.createElement(
                            !this.useTh ? 'td' : 'th',
                        );
                        td.textContent = cell;
                        tr.appendChild(td);
                    }
                });
                container.appendChild(tr);
            });
        });
    }
}

@Component({
    selector: 'cmps-screen-reader-table',
    exportAs: 'cmpsScreenReaderTable',
    template: ` <table
        [attr.role]="options?.role || 'table'"
        [attr.aria-label]="options?.label"
        [attr.aria-colcount]="columnCount"
        [attr.aria-rowcount]="rowCount"
    >
        <thead>
            <ng-container
                *cmpsScreenReaderTableSection="data?.headerRows; useTh: true"
            ></ng-container>
        </thead>
        <tbody>
            <ng-container
                *cmpsScreenReaderTableSection="data?.bodyRows"
            ></ng-container>
        </tbody>
        <tfoot>
            <ng-container
                *cmpsScreenReaderTableSection="data?.footerRows; useTh: true"
            ></ng-container>
        </tfoot>
    </table>`,
})
export class ScreenReaderTableComponent {
    @Input() options: ScreenReaderTableOptions;
    @Input() data: ScreenReaderTableData;

    get columnCount(): number {
        return !this.data
            ? 0
            : Math.max(
                  !this.data.headerRows
                      ? 0
                      : this.data.headerRows.reduce(
                            (p, c) => Math.max(p, c.length),
                            0,
                        ),
                  !this.data.footerRows
                      ? 0
                      : this.data.footerRows.reduce(
                            (p, c) => Math.max(p, c.length),
                            0,
                        ),
              );
    }

    get rowCount(): number {
        return !this.data
            ? 0
            : (!this.data.headerRows ? 0 : this.data.headerRows.length) +
                  (!this.data.footerRows ? 0 : this.data.footerRows.length) +
                  (!this.data.bodyRows ? 0 : this.data.bodyRows.length);
    }
}
